import React, { useEffect, useMemo, useState } from "react";
import "../StockInward.css";
import {
  addEmployeeMaster,
  deleteEmployeeMaster,
  getEmployeeMasters,
  updateEmployeeMaster,
} from "../storage";
import { formatDisplayDate } from "../utils/dateFormat";

function makeDefaultForm() {
  const now = new Date();
  const joiningDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
    now.getDate()
  ).padStart(2, "0")}`;
  return { name: "", perDaySalary: "", joiningDate, active: "active" };
}

export default function EmployeeMaster() {
  const [form, setForm] = useState(() => makeDefaultForm());
  const [employeeMasters, setEmployeeMasters] = useState([]);
  const [query, setQuery] = useState("");
  const [editingId, setEditingId] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState({});

  const refresh = async () => {
    try {
      const list = await getEmployeeMasters();
      const next = Array.isArray(list) ? list : [];
      next.sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || "")));
      setEmployeeMasters(next);
    } catch (err) {
      console.error(err);
      alert("Failed to load employee masters.");
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredEntries = useMemo(() => {
    const q = String(query || "").trim().toLowerCase();
    if (!q) return employeeMasters;
    return employeeMasters.filter((entry) => {
      const name = String(entry?.name || "").toLowerCase();
      const perDaySalary = String(entry?.perDaySalary ?? "").toLowerCase();
      const joiningDate = String(entry?.joiningDate || "").toLowerCase();
      const status = entry?.active ? "active" : "inactive";
      return name.includes(q) || perDaySalary.includes(q) || joiningDate.includes(q) || status.includes(q);
    });
  }, [employeeMasters, query]);

  const resetForm = () => {
    setForm(makeDefaultForm());
    setEditingId("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = String(form.name || "").trim();
    const perDaySalaryValue = String(form.perDaySalary ?? "").trim();
    const joiningDate = String(form.joiningDate || "").trim();
    const active = String(form.active || "active").toLowerCase() !== "inactive";
    const perDaySalary = Number(perDaySalaryValue);
    if (!name) {
      alert("Employee name is required.");
      return;
    }
    if (perDaySalaryValue === "" || !Number.isFinite(perDaySalary) || perDaySalary < 0) {
      alert("Per day salary must be a valid non-negative amount.");
      return;
    }
    if (!joiningDate) {
      alert("Joining date is required.");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await updateEmployeeMaster(editingId, { name, perDaySalary, joiningDate, active });
      } else {
        await addEmployeeMaster({ name, perDaySalary, joiningDate, active });
      }
      resetForm();
      await refresh();
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to save employee master.");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (entry) => {
    if (!entry?.id) return;
    setEditingId(entry.id);
    setForm({
      name: String(entry.name || ""),
      perDaySalary:
        entry?.perDaySalary === null || entry?.perDaySalary === undefined
          ? ""
          : String(entry.perDaySalary),
      joiningDate: String(entry?.joiningDate || makeDefaultForm().joiningDate),
      active: entry?.active ? "active" : "inactive",
    });
    try {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      // ignore
    }
  };

  const handleDelete = async (entry) => {
    if (!entry?.id) return;
    if (!window.confirm(`Delete employee "${entry.name || "entry"}"?`)) return;
    setDeleting((prev) => ({ ...prev, [entry.id]: true }));
    try {
      await deleteEmployeeMaster(entry.id);
      if (editingId === entry.id) {
        resetForm();
      }
      await refresh();
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to delete employee master.");
    } finally {
      setDeleting((prev) => {
        const next = { ...prev };
        delete next[entry.id];
        return next;
      });
    }
  };

  return (
    <div className="stock-inward-container" style={{ paddingTop: 0 }}>
      <div className="form-card">
        <div className="card-header">
          <h2 className="card-title">Employee Master</h2>
          <span className="entry-count">{editingId ? "Edit Mode" : "Employee Setup"}</span>
        </div>
        <form className="inward-form" onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="field full fl-field">
              <input
                id="employee-master-name"
                className="fl-input input-field"
                type="text"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder=" "
                required
              />
              <label htmlFor="employee-master-name" className="fl-label">
                Employee Name
              </label>
            </div>
            <div className="field full fl-field">
              <input
                id="employee-master-per-day-salary"
                className="fl-input input-field"
                type="number"
                min="0"
                step="0.01"
                value={form.perDaySalary}
                onChange={(e) => setForm((prev) => ({ ...prev, perDaySalary: e.target.value }))}
                placeholder=" "
                required
              />
              <label htmlFor="employee-master-per-day-salary" className="fl-label">
                Per Day Salary
              </label>
            </div>
            <div className="field full fl-field">
              <input
                id="employee-master-joining-date"
                className="fl-input input-field"
                type="date"
                value={form.joiningDate}
                onChange={(e) => setForm((prev) => ({ ...prev, joiningDate: e.target.value }))}
                placeholder=" "
                required
              />
              <label htmlFor="employee-master-joining-date" className="fl-label">
                Joining Date
              </label>
            </div>
            <div className="field full fl-field">
              <select
                id="employee-master-status"
                className="fl-input input-field"
                value={form.active}
                onChange={(e) => setForm((prev) => ({ ...prev, active: e.target.value }))}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <label htmlFor="employee-master-status" className="fl-label">
                Status
              </label>
            </div>
          </div>
          <div className="form-actions">
            {editingId ? (
              <>
                <button type="button" className="btn-secondary" onClick={resetForm} disabled={saving}>
                  Cancel Edit
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? "Updating..." : "Update Employee"}
                </button>
              </>
            ) : (
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? "Saving..." : "Add Employee"}
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="entries-card">
        <div className="card-header" style={{ borderBottom: "none" }}>
          <h2 className="card-title">Saved Employees</h2>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search employee name, salary, joining date or status..."
            className="input-field"
            style={{ maxWidth: 280 }}
          />
        </div>
        <div className="entries-table-container">
          <table className="entries-table" style={{ minWidth: 1080 }}>
            <thead>
              <tr>
                <th>Employee Name</th>
                <th style={{ width: 180 }}>Per Day Salary</th>
                <th style={{ width: 180 }}>Joining Date</th>
                <th style={{ width: 140 }}>Status</th>
                <th style={{ width: 180 }}>Created At</th>
                <th style={{ width: 160 }} className="text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredEntries.length === 0 ? (
                <tr>
                  <td colSpan={6} className="empty-state" style={{ padding: 16 }}>
                    No employee names found
                  </td>
                </tr>
              ) : (
                filteredEntries.map((entry) => (
                  <tr key={entry.id}>
                    <td>{entry.name}</td>
                    <td>{Number(entry?.perDaySalary || 0).toFixed(2)}</td>
                    <td>{entry.joiningDate ? formatDisplayDate(entry.joiningDate) : "-"}</td>
                    <td>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minWidth: 80,
                          padding: "0.28rem 0.65rem",
                          borderRadius: 999,
                          fontSize: "0.78rem",
                          fontWeight: 800,
                          color: "#ffffff",
                          background: entry?.active
                            ? "linear-gradient(135deg, #059669, #10b981)"
                            : "linear-gradient(135deg, #64748b, #475569)",
                        }}
                      >
                        {entry?.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>{entry.createdAt ? formatDisplayDate(entry.createdAt) : "-"}</td>
                    <td className="text-right">
                      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={() => startEdit(entry)}
                          style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
                          disabled={!!deleting[entry.id]}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn-remove"
                          onClick={() => handleDelete(entry)}
                          style={{ width: 32, height: 32, padding: 0 }}
                          disabled={!!deleting[entry.id]}
                        >
                          x
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
